import { LoadStatus } from '@servicetitan/cp-ui';
import { FormValidators, InputFieldState } from '@servicetitan/form-state';
import { inject, injectable } from '@servicetitan/react-ioc';
import { AuthApi, SendPasswordResetLinkRequest } from 'api';
import { FormState } from 'formstate';
import { computed, makeObservable, observable, runInAction } from 'mobx';
import { SpecialSettingsStore } from '../../common/stores/special-settings.store';
import { ContactInfoMode, validateContactInfo } from '../utils/contact-info.validator';
import {
    contactInfoModeFromAllowed,
    normalizeContactForSubmit,
} from '../utils/contact-method-mode';

const SIGNUP_CONTACT_METHODS_KEY = 'signup-contact-methods';

interface SignupContactMethodsSettings {
    allowedContactMethods?: unknown;
}

/** Required-field message per mode (AC-5: required check fires first). */
const requiredMessageByMode: Record<ContactInfoMode, string> = {
    'email-only': 'Enter an email',
    'phone-only': 'Enter a phone number',
    'either': 'Enter an email or phone number',
};

/** Format-error message per mode. */
const formatErrorByMode: Record<ContactInfoMode, string> = {
    'email-only': 'Enter a valid email address',
    'phone-only': 'Enter a valid phone number',
    'either': 'Enter a valid email or phone number',
};

@injectable()
export class AuthRecoveryRequestPasswordStore {
    @observable status = LoadStatus.None;
    @observable contactInfoMode: ContactInfoMode = 'email-only';

    @computed get canSubmit() {
        return !!this.form.$.contact.value && !this.form.hasError;
    }

    form = new FormState({
        contact: new InputFieldState('')
            .validators(
                /*
                 * Required check FIRST — formstate short-circuits in order.
                 * Arrow closures read this.contactInfoMode lazily at validate time.
                 */
                (val: string) =>
                    FormValidators.requiredWithCustomMessage(
                        requiredMessageByMode[this.contactInfoMode]
                    )(val),
                (val: string) => {
                    const result = validateContactInfo(val, this.contactInfoMode);
                    if (result.isValid) {
                        return false;
                    }
                    if (result.kind === 'phone' && result.country === 'unsupported') {
                        return 'Phone numbers from this country are not supported';
                    }
                    return formatErrorByMode[this.contactInfoMode];
                }
            )
            .disableAutoValidation(),
    });

    constructor(
        @inject(AuthApi) private authApi: AuthApi,
        @inject(SpecialSettingsStore) private specialSettings: SpecialSettingsStore
    ) {
        makeObservable(this);
        this.loadContactMode();
    }

    submit = async () => {
        const result = await this.form.validate();

        if (result.hasError) {
            return;
        }

        runInAction(() => (this.status = LoadStatus.Loading));

        try {
            await this.authApi.client.selfSendPasswordResetLink(
                new SendPasswordResetLinkRequest({
                    username: normalizeContactForSubmit(
                        this.form.$.contact.value,
                        this.contactInfoMode
                    ),
                })
            );

            runInAction(() => (this.status = LoadStatus.Ok));
        } catch {
            runInAction(() => (this.status = LoadStatus.Error));
        }
    };

    private async loadContactMode() {
        const raw = await this.specialSettings.getAsync<SignupContactMethodsSettings>(
            SIGNUP_CONTACT_METHODS_KEY
        );

        if (this.specialSettings.failed) {
            // fail-closed: keep 'email-only', no banner
            return;
        }

        const settings = raw ?? {};
        runInAction(() => {
            this.contactInfoMode = contactInfoModeFromAllowed(settings.allowedContactMethods);
        });
    }
}
